import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ImageUploader } from "@/components/ImageUploader";
import {
  Leaf,
  Package,
  ShoppingCart,
  MessageSquare,
  Users,
  FileText,
  LogOut,
  ChevronRight,
  AlertCircle,
  Image,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const auth = sessionStorage.getItem("adminAuth");
  if (!auth) return null;
  return { Authorization: `Basic ${auth}` };
};

export const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoUrl, setLogoUrl] = useState("");

  useEffect(() => {
    const headers = getAuthHeaders();
    if (!headers) {
      navigate("/admin");
      return;
    }

    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/admin/stats`, { headers });
        setStats(response.data);
        const siteRes = await axios.get(`${API}/site-settings`);
        setLogoUrl(siteRes.data?.logo_url || "");
      } catch (error) {
        if (error.response?.status === 401) {
          sessionStorage.removeItem("adminAuth");
          navigate("/admin");
        } else {
          toast.error("Failed to load dashboard data");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [navigate]);

  const saveLogo = async (url) => {
    const headers = getAuthHeaders();
    try {
      await axios.put(`${API}/admin/site-settings`, { logo_url: url }, { headers });
      setLogoUrl(url);
      toast.success(url ? "Logo updated!" : "Logo removed!");
    } catch (error) {
      toast.error("Failed to update logo");
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("adminAuth");
    toast.success("Logged out successfully");
    navigate("/admin");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-deep-moss font-serif text-xl">Loading...</div>
      </div>
    );
  }

  const statCards = [
    {
      label: "Products",
      value: stats?.products || 0,
      icon: Package,
      link: "/admin/products",
      color: "bg-deep-moss",
    },
    {
      label: "Orders",
      value: stats?.orders || 0,
      icon: ShoppingCart,
      link: "/admin/orders",
      color: "bg-terracotta",
      alert: stats?.pending_orders > 0 ? `${stats.pending_orders} pending` : null,
    },
    {
      label: "Messages",
      value: stats?.messages || 0,
      icon: MessageSquare,
      link: "/admin/messages",
      color: "bg-stone-600",
      alert: stats?.unread_messages > 0 ? `${stats.unread_messages} unread` : null,
    },
    {
      label: "Blog Posts",
      value: stats?.blog_posts || 0,
      icon: FileText,
      link: "/admin/blog",
      color: "bg-amber-600",
    },
    {
      label: "Images",
      value: stats?.images || 0,
      icon: Image,
      link: "/admin/images",
      color: "bg-purple-600",
    },
    {
      label: "Subscribers",
      value: stats?.subscribers || 0,
      icon: Users,
      link: "/admin/subscribers",
      color: "bg-blue-600",
    },
  ];

  return (
    <div className="min-h-screen bg-bone-cream animate-fade-in">
      {/* Header */}
      <header className="bg-deep-moss text-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <Leaf className="w-6 h-6" />
              <span className="font-serif text-xl font-semibold">WEH Admin</span>
            </div>
            <div className="flex items-center gap-4">
              <Link
                to="/"
                data-testid="view-store-link"
                className="font-sans text-sm text-white/80 hover:text-white"
              >
                View Store
              </Link>
              <button
                onClick={handleLogout}
                data-testid="logout-btn"
                className="flex items-center gap-2 font-sans text-sm text-white/80 hover:text-white"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-8">
        <h1 className="font-serif text-3xl font-semibold text-stone-900 mb-8">Dashboard</h1>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-12">
          {statCards.map((stat) => {
            const Icon = stat.icon;
            return (
              <Link
                key={stat.label}
                to={stat.link}
                data-testid={`stat-card-${stat.label.toLowerCase()}`}
                className="bg-white p-6 border border-stone-200 hover:shadow-md transition-shadow"
              >
                <div className={`w-10 h-10 ${stat.color} rounded-full flex items-center justify-center mb-4`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <p className="font-sans text-2xl font-semibold text-stone-900 mb-1">
                  {stat.value}
                </p>
                <p className="font-sans text-sm text-stone-600">{stat.label}</p>
                {stat.alert && (
                  <p className="font-sans text-xs text-terracotta mt-2 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {stat.alert}
                  </p>
                )}
              </Link>
            );
          })}
        </div>

        {/* Site Logo / Branding */}
        <div className="bg-white p-6 border border-stone-200 mb-12" data-testid="site-logo-section">
          <h2 className="font-serif text-xl font-semibold text-stone-900 mb-2">Site Logo</h2>
          <p className="font-sans text-sm text-stone-500 mb-4">
            Upload your logo to replace the site name in the header. A wide/horizontal PNG with a transparent background works best (recommended height ~80px).
          </p>
          <div className="max-w-md" data-testid="logo-uploader">
            <ImageUploader currentImage={logoUrl} onUpload={saveLogo} />
          </div>
          {logoUrl && (
            <button
              onClick={() => saveLogo("")}
              data-testid="remove-logo-btn"
              className="mt-3 font-sans text-sm text-red-500 hover:text-red-600"
            >
              Remove logo (revert to site name)
            </button>
          )}
        </div>

        {/* Quick Actions */}
        <h2 className="font-serif text-xl font-semibold text-stone-900 mb-4">Quick Actions</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Add New Product", link: "/admin/products/new", icon: Package },
            { label: "Create Blog Post", link: "/admin/blog/new", icon: FileText },
            { label: "Upload Images", link: "/admin/images", icon: Image },
            { label: "View Orders", link: "/admin/orders", icon: ShoppingCart },
          ].map((action) => {
            const Icon = action.icon;
            return (
              <Link
                key={action.label}
                to={action.link}
                data-testid={`action-${action.label.toLowerCase().replace(/\s/g, "-")}`}
                className="flex items-center justify-between bg-white p-4 border border-stone-200 hover:border-deep-moss transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <Icon className="w-5 h-5 text-stone-600 group-hover:text-deep-moss" />
                  <span className="font-sans text-sm text-stone-900">{action.label}</span>
                </div>
                <ChevronRight className="w-4 h-4 text-stone-400 group-hover:text-deep-moss" />
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};
